import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { CartCharge, setCartCharges } from 'state/slices/shoppingCartSlice';
import { updateAddon } from 'state/slices/unitSelectionSlice';
import { RootState } from 'state/store';

import { createPersistAddOnsHandler } from 'components/render-page/pages/options-page/persistAddOns';
import { AddonOption } from 'types/addon';

import { useActiveStepIndexFromRedux, useGetActiveStepIndex } from '../useGetActiveStepIndex';

import { useSaveOptionsPageSynchronously } from './useSaveOptionsPageSynchronously';

export interface UseAddonOptionsPersistSyncResult {
  /** Local loading flag while `persistAddOns` runs (skipped in preview). */
  isLoading: boolean;
  /** Persists addon selection via options-page PATCH; returns false on failure. */
  persistAddOns: (addOns: AddonOption[]) => Promise<boolean>;
  /**
   * Awaits persist, then dispatches `updateAddon` and `setCartCharges`.
   * In preview mode, persist is a no-op that resolves to true.
   * @returns false if persist failed; true after Redux was updated.
   */
  syncAddonSelectionToBackendAndDispatch: (
    nextAddOns: AddonOption[],
    nextCharges: CartCharge[],
  ) => Promise<boolean>;
}

/**
 * Shared wiring for options-page addon flows: active step index, synchronous PATCH save,
 * and Redux updates. Use in `OptionsList`, `MultipleAddOnOption`, etc.
 */
export function useAddonOptionsPersistSync(): UseAddonOptionsPersistSyncResult {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { isLeadProfile, isPreviewMode } = useSelector(
    (state: RootState) => state.auth ?? { isLeadProfile: false, isPreviewMode: false },
  );

  const getActiveStepIndex = useGetActiveStepIndex();
  const getLeadProfileStepIndex = useActiveStepIndexFromRedux();

  const activeStepIndex = isLeadProfile ? getLeadProfileStepIndex : getActiveStepIndex();

  const { saveOptionsPageSynchronously, isLoading } = useSaveOptionsPageSynchronously({
    activeStepIndex,
  });

  const persistAddOns = useMemo(
    () =>
      createPersistAddOnsHandler({
        activeStepIndex,
        saveOptionPage: saveOptionsPageSynchronously,
        isPreviewMode,
      }),
    [activeStepIndex, saveOptionsPageSynchronously, isPreviewMode],
  );

  const syncAddonSelectionToBackendAndDispatch = useCallback(
    async (nextAddOns: AddonOption[], nextCharges: CartCharge[]) => {
      const isSuccess = await persistAddOns(nextAddOns);
      if (!isSuccess) return false;
      dispatch(updateAddon(nextAddOns));
      dispatch(setCartCharges(nextCharges));
      queryClient.invalidateQueries({ queryKey: ['add-ons'] }).catch(() => undefined);
      return true;
    },
    [dispatch, persistAddOns, queryClient],
  );

  return {
    isLoading,
    persistAddOns,
    syncAddonSelectionToBackendAndDispatch,
  };
}
